import { Box, Typography } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import { colors } from '../../../../theme/palette';
import { ChartContent } from './ChartContent';
import Highcharts, { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Empty } from './Empty';
import { LANGUAGE_MAP } from '../../../../store/models/location/Language';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		cardLabel: {
			display: 'flex',
			justifyContent: 'space-between',
			margin: '10px 0px 20px',
		},
		cardLabelTitle: {
			color: colors.neutralDark,
			fontSize: '14px',
			lineHeight: '22px',
			display: 'flex',
			alignItems: 'center',
		},
		cardLabelValue: {
			color: colors.purpleAccent,
			fontSize: '16px',
			lineHeight: '24px',
			fontWeight: 700,
			marginLeft: '5px',
		},
	})
);

export type LanguageResponseType = {
	name: string;
	value: number;
};

export type LanguagesAccessedProps = {
	languageData: LanguageResponseType[];
	totalResponses: number;
};

export const LanguagesAccessedCard = (props: LanguagesAccessedProps) => {
	const { languageData, totalResponses } = props;
	const classes = useStyles();

	// map language name to language map
	const mappedLanguageData = languageData.map((data) => ({
		name: LANGUAGE_MAP[data.name],
		value: data.value,
	}));

	const sortedLanguageData = [...mappedLanguageData]?.sort((a, b) => b.value - a.value);

	const chartOptions: Options = {
		chart: {
			height: 300,
			type: 'bar',
			zoomType: 'xy',
			panning: {
				enabled: true,
				type: 'xy',
			},
			panKey: 'shift',
			animation: {
				duration: 500,
			},
		},
		colors: [
			'#58C9E6',
			'#78479E',
			'#2C3F61',
			'#DED0E9',
			'#D2EDF4',
			'#0339E1',
			'#388D5D',
			'#781BEF',
			'#BBBBBB',
			'#42BCF5',
			'#4BE061',
			'#717171',
			'#D635A0',
		],

		title: {
			text: '',
		},
		series: [
			{
				type: 'bar',
				data: sortedLanguageData?.map((data) => data.value),
				colorByPoint: true,
				dataLabels: {
					enabled: true,
				},
			},
		],
		xAxis: {
			categories: sortedLanguageData?.map((data) => data.name),
			labels: {
				enabled: true,
				useHTML: true,
				formatter: function () {
					return `${this.value}`;
				},
				style: {
					color: colors.grayDark,
					fontSize: '12px',
					lineHeight: '20px',
				},
			},
		},
		yAxis: {
			opposite: true,
			tickPixelInterval: 150,
			title: { text: null },
		},
		credits: {
			enabled: false,
		},
		tooltip: {
			useHTML: true,
			backgroundColor: colors.grayDark,
			borderWidth: 0,
			borderRadius: 4,
			shadow: false,
			style: {
				padding: '8px 12px',
				color: colors.pureWhite,
				fontSize: '16px',
				fontWeight: '700',
				whiteSpace: 'nowrap',
			},
			formatter: function () {
				return `${this.y}`;
			},
		},
		legend: { enabled: false },
		plotOptions: {
			series: {
				animation: false,
				borderWidth: 0,
				cursor: 'pointer',
				dataLabels: {
					enabled: true,
				},
			},
		},
		responsive: {
			rules: [
				{
					condition: {
						maxWidth: 550,
					},
					chartOptions: {
						xAxis: {
							visible: true,
						},
						subtitle: {
							x: 0,
						},
						plotOptions: {
							series: {
								dataLabels: [
									{
										enabled: true,
										y: 8,
									},
									{
										enabled: true,
										format: '{point.name}',
										y: -8,
										style: {
											fontWeight: 'normal',
											opacity: 0.7,
										},
									},
								],
							},
						},
					},
				},
			],
		},
	};

	return (
		<ChartContent
			name={'LANGUAGES ACCESSED'}
			toolTipMsg={'Shows the distribution of languages accessed in your app.'}
		>
			<Box className={classes.cardLabel}>
				<Typography className={classes.cardLabelTitle}>
					{totalResponses > 1 ? 'Total Languages Accessed:' : 'Total Language Accessed:'}
					<span className={classes.cardLabelValue}>{totalResponses}</span>
				</Typography>
			</Box>
			{languageData.length === 0 ? <Empty /> : <HighchartsReact highcharts={Highcharts} options={chartOptions} />}
		</ChartContent>
	);
};
